import type { TabelaSchematu } from '@/lib/baza-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PODPOWIADANIE NAZW TABEL I KOLUMN W EDYTORZE — „Działania na bazie" (29.09.2026).
 *
 *  ⚠️⚠️ Z PLANU ETAPU 1 („edytor SQL z podpowiadaniem nazw tabel i kolumn"), który zszedł
 *  z samym polem tekstowym. Zgłoszenie, od którego wszystko się zaczęło: „trzeba by znać dużo
 *  z głowy, jak wyglądają tabele" — schemat SPS to ~130 tabel i ~2600 kolumn.
 *
 *  ⚠️ PODPOWIADAMY Z SCHEMATU WYBRANEJ BAZY (drzewo po lewej), bez pytania serwera o każdy znak.
 *  Kolumny tylko z tabel użytych w zapytaniu — wszystkie 2600 naraz byłyby szumem, nie pomocą.
 *
 *  ⚠️ W NAPISIE I KOMENTARZU CISZA: `WHERE DESCRIPTION LIKE 'Ręk` to dane, a lista wyskakująca
 *  w środku napisu zjadałaby Enter, którym ktoś chce po prostu zakończyć linię.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type Podpowiedz = { tekst: string; rodzaj: 'TABELA' | 'WIDOK' | 'KOLUMNA'; opis: string };

const ZNAK = /[A-Za-z0-9_$#@]/;

/** Czy kursor stoi w kodzie — a nie w napisie, nazwie w cudzysłowie albo komentarzu. */
export function wKodzie(tekst: string, kursor: number): boolean {
  let i = 0;
  while (i < kursor) {
    const c = tekst[i];
    const d = tekst[i + 1];
    if (c === '-' && d === '-') {
      const j = tekst.indexOf('\n', i + 2);
      if (j === -1 || j >= kursor) return false;
      i = j + 1;
      continue;
    }
    if (c === '/' && d === '*') {
      /** Komentarze blokowe T-SQL bywają zagnieżdżone — liczymy głębokość jak sito odczytu. */
      let glebokosc = 1;
      let j = i + 2;
      while (j < tekst.length && glebokosc > 0) {
        if (tekst[j] === '/' && tekst[j + 1] === '*') {
          glebokosc += 1;
          j += 2;
        } else if (tekst[j] === '*' && tekst[j + 1] === '/') {
          glebokosc -= 1;
          j += 2;
        } else {
          j += 1;
        }
      }
      if (glebokosc > 0 || j > kursor) return false;
      i = j;
      continue;
    }
    if (c === "'" || c === '"') {
      let j = i + 1;
      for (;;) {
        const k = tekst.indexOf(c, j);
        if (k === -1 || k >= kursor) return false;
        /** Podwojony znak to jego zapis wewnątrz napisu (`''`), nie koniec. */
        if (tekst[k + 1] === c) {
          j = k + 2;
          continue;
        }
        i = k + 1;
        break;
      }
      continue;
    }
    i += 1;
  }
  return true;
}

/** Słowo pod kursorem (całe, także za kursorem) i to, co stoi przed kropką: `i.ITE` → `i`. */
export function slowoPrzyKursorze(
  tekst: string,
  kursor: number,
): { od: number; do: number; prefiks: string; kwalifikator: string | null } {
  let od = kursor;
  while (od > 0 && ZNAK.test(tekst[od - 1]!)) od -= 1;
  let koniec = kursor;
  while (koniec < tekst.length && ZNAK.test(tekst[koniec]!)) koniec += 1;

  let kwalifikator: string | null = null;
  /** `x.[Ite` — nawias otwierający przed słowem nie przerywa kwalifikatora. */
  const przedSlowem = tekst[od - 1] === '[' ? od - 1 : od;
  if (tekst[przedSlowem - 1] === '.') {
    const kropka = przedSlowem - 1;
    if (tekst[kropka - 1] === ']') {
      const start = tekst.lastIndexOf('[', kropka - 1);
      if (start >= 0) kwalifikator = tekst.slice(start + 1, kropka - 1);
    } else {
      let s = kropka;
      while (s > 0 && ZNAK.test(tekst[s - 1]!)) s -= 1;
      if (s < kropka) kwalifikator = tekst.slice(s, kropka);
    }
  }
  return { od, do: koniec, prefiks: tekst.slice(od, kursor), kwalifikator };
}

/** Słowa, które po nazwie tabeli nie są aliasem (`FROM Items WHERE …`). */
const NIE_ALIAS = new Set([
  'WHERE', 'ON', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER', 'GROUP', 'ORDER', 'HAVING',
  'UNION', 'EXCEPT', 'INTERSECT', 'WITH', 'OPTION', 'AS', 'SELECT', 'FROM', 'AND', 'OR', 'NOT', 'APPLY',
  'PIVOT', 'UNPIVOT', 'TOP', 'NOLOCK',
]);

/**
 * TABELE UŻYTE W ZAPYTANIU — po nazwie i po aliasie: `FROM Items i`, `JOIN [dbo].[Users] AS u`,
 * `FROM Items, Rationing r`. Przecinek z listy kolumn też pasuje do wzorca, ale odpada, bo po nim
 * nie stoi nazwa tabeli ze schematu.
 */
export function tabeleZapytania(tekst: string, tabele: readonly TabelaSchematu[]): Map<string, TabelaSchematu> {
  const wgNazwy = new Map(tabele.map((t) => [t.nazwa.toLowerCase(), t]));
  const wynik = new Map<string, TabelaSchematu>();
  const wzor =
    /(?:\bFROM|\bJOIN|,)\s*(?:\[?dbo\]?\s*\.\s*)?\[?([A-Za-z_][A-Za-z0-9_$#@]*)\]?(?:\s+(?:AS\s+)?\[?([A-Za-z_][A-Za-z0-9_]*)\]?)?/gi;
  for (const m of tekst.matchAll(wzor)) {
    const t = wgNazwy.get(m[1]!.toLowerCase());
    if (t === undefined) continue;
    wynik.set(t.nazwa.toLowerCase(), t);
    const alias = m[2];
    if (alias !== undefined && !NIE_ALIAS.has(alias.toUpperCase())) wynik.set(alias.toLowerCase(), t);
  }
  return wynik;
}

/** Po tych słowach człowiek wpisuje nazwę tabeli — wtedy tabele idą przed kolumnami. */
const PO_NICH_TABELA = /\b(FROM|JOIN|APPLY)\s*$/i;

/**
 * PODPOWIEDZI DLA KURSORA — `null`, gdy lista nie ma się pokazać.
 *
 * ⚠️ BEZ WPISANEJ LITERY LISTA SIĘ NIE OTWIERA SAMA (tylko `wymus`, czyli Ctrl+Spacja) — spacja
 * po każdym słowie otwierałaby ją w kółko. Wyjątek: tuż po kropce (`i.`) — tam lista kolumn
 * jest dokładnie tym, czego człowiek szuka.
 */
export function podpowiedzi(
  tekst: string,
  kursor: number,
  tabele: readonly TabelaSchematu[],
  opcje: { wymus?: boolean; limit?: number } = {},
): { od: number; do: number; lista: Podpowiedz[] } | null {
  const limit = opcje.limit ?? 12;
  if (tabele.length === 0 || !wKodzie(tekst, kursor)) return null;
  const s = slowoPrzyKursorze(tekst, kursor);
  const pre = s.prefiks.toLowerCase();
  const uzyte = tabeleZapytania(tekst, tabele);
  const kolumnyZ = (t: TabelaSchematu): Podpowiedz[] =>
    t.kolumny.map((k) => ({ tekst: k.nazwa, rodzaj: 'KOLUMNA', opis: `${t.nazwa} · ${k.typ}` }));
  const wszystkieTabele = (): Podpowiedz[] =>
    tabele.map((t) => ({ tekst: t.nazwa, rodzaj: t.widok ? 'WIDOK' : 'TABELA', opis: '' }));

  let kandydaci: Podpowiedz[];
  if (s.kwalifikator !== null) {
    const kw = s.kwalifikator.toLowerCase();
    if (kw === 'dbo') {
      kandydaci = wszystkieTabele();
    } else {
      const t = uzyte.get(kw) ?? tabele.find((x) => x.nazwa.toLowerCase() === kw);
      if (t === undefined) return null;
      kandydaci = kolumnyZ(t);
    }
  } else {
    if (pre === '' && opcje.wymus !== true) return null;
    const kolumny = [...new Set(uzyte.values())].flatMap(kolumnyZ);
    kandydaci = PO_NICH_TABELA.test(tekst.slice(0, s.od)) ? [...wszystkieTabele(), ...kolumny] : [...kolumny, ...wszystkieTabele()];
  }

  /** Najpierw pasujące od początku, potem (od dwóch liter) zawierające — ta sama nazwa raz. */
  const naPoczatku = kandydaci.filter((p) => p.tekst.toLowerCase().startsWith(pre));
  const wSrodku = pre.length >= 2 ? kandydaci.filter((p) => !p.tekst.toLowerCase().startsWith(pre) && p.tekst.toLowerCase().includes(pre)) : [];
  const byly = new Set<string>();
  const lista: Podpowiedz[] = [];
  for (const p of [...naPoczatku, ...wSrodku]) {
    const klucz = `${p.rodzaj === 'KOLUMNA' ? 'K' : 'T'}:${p.tekst.toLowerCase()}`;
    if (byly.has(klucz)) continue;
    byly.add(klucz);
    lista.push(p);
    if (lista.length >= limit) break;
  }
  /** Wpisane słowo jest już pełną nazwą i nic innego nie pasuje — lista byłaby szumem. */
  if (lista.length === 0 || (lista.length === 1 && lista[0]!.tekst.toLowerCase() === pre)) return null;
  return { od: s.od, do: s.do, lista };
}
